import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ArrowRight, ExternalLink, Heart, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LatestReleases from "../components/LatestReleases";
import ReleaseModal from "../components/ReleaseModal";
import ReleaseCard, { CoverImage } from "../components/ReleaseCard";
import { useSettings } from "../context/SettingsContext";
import PartnerSpot from "../components/PartnerSpot";
import api from "../utils/api";
import { getLocalizedText } from "../utils/localizedText";

/* ── Hero Search Bar ─────────────────────────── */
function HeroSearch() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    else navigate("/search");
  };
  return (
    <form onSubmit={submit} className="w-full max-w-xl mx-auto">
      <div className="flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden shadow-2xl focus-within:border-rose-400/60 transition-all">
        <Search size={18} className="ml-4 text-zinc-400 flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("home.hero_search_placeholder")}
          data-testid="hero-search-input"
          className="flex-1 bg-transparent py-3.5 px-3 text-white text-sm font-medium placeholder-zinc-500 outline-none"
        />
        <button
          type="submit"
          data-testid="hero-search-submit"
          className="px-5 py-3.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-colors flex-shrink-0 flex items-center gap-1.5"
        >
          {t("home.hero_search_btn")} <ArrowRight size={14} />
        </button>
      </div>
    </form>
  );
}

/* ── Top Artists Scroll ─────────────────────── */
function nameToSlug(name) {
  return name.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .slice(0, 80);
}

/* ── Artist Spotlight (weekly rotating, 1 featured artist + releases) ── */
function ArtistSpotlightSection({ onOpen }) {
  const [artist, setArtist] = useState(null);
  const [releases, setReleases] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/artists/spotlight?limit=1")
      .then(async res => {
        const a = res.data?.[0];
        if (!a) return;
        setArtist(a);
        const detail = await api.get(`/artists/${a.slug}`);
        setReleases((detail.data.releases || []).slice(0, 8));
      })
      .catch(() => {});
  }, []);

  if (!artist) return null;

  return (
    <section
      className="py-14 border-t border-white/5"
      style={{ background: "#080808", backgroundImage: "radial-gradient(circle at 85% 50%, rgba(225,29,72,0.07) 0%, transparent 55%)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-1">{t("home.spotlight_eyebrow")}</p>
            <h2 className="text-2xl sm:text-3xl font-black font-heading text-white">{t("home.spotlight_title")}</h2>
          </div>
          <Link to="/artists" className="text-zinc-500 hover:text-rose-400 text-sm font-semibold transition-colors flex items-center gap-1">
            {t("home.all_artists")} <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid grid-cols-[5fr_7fr] gap-4 sm:gap-6 items-stretch">
          {/* Left: Artist Card */}
          <Link
            to={`/artist/${artist.slug}`}
            data-testid="spotlight-artist-card"
            className="group relative rounded-2xl overflow-hidden aspect-square bg-zinc-900 block"
          >
            {artist.bild_url ? (
              <img src={artist.bild_url} alt={artist.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-7xl font-black text-zinc-700 font-heading bg-zinc-900">
                {artist.name[0]}
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
              <p className="text-rose-400 text-xs font-bold uppercase tracking-widest mb-1">Featured</p>
              <div className="flex items-center gap-2">
                <p className="text-white font-black text-xl sm:text-2xl font-heading leading-tight">{artist.name}</p>
                {artist.linked_user_ids?.length > 0 && (
                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
                      boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 2px 8px rgba(59,130,246,0.6)",
                    }}>
                    <svg viewBox="0 0 12 12" fill="none" style={{ width: "8px", height: "8px" }}>
                      <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </div>
              {releases.length > 0 && (
                <p className="text-zinc-400 text-xs mt-1">{releases.length} Releases</p>
              )}
            </div>
          </Link>

          {/* Right: Last releases – fill the same height */}
          <div className="flex flex-col overflow-hidden gap-0.5">
            {releases.map((r) => (
              <button
                key={r.id}
                onClick={() => onOpen?.(r)}
                data-testid={`spotlight-release-${r.id}`}
                className="flex items-center gap-3 px-2 sm:px-3 py-2 rounded-xl hover:bg-zinc-900 transition-colors group text-left flex-1 min-h-0"
              >
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                  {r.cover_url && <img src={r.cover_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs sm:text-sm font-semibold truncate group-hover:text-rose-300 transition-colors">
                    {r.titel}
                  </p>
                  <p className="text-zinc-500 text-xs truncate">{r.interpret} · {r.erscheinungsdatum?.slice(0, 4)}</p>
                </div>
                {r.typ && <span className="text-zinc-700 text-xs flex-shrink-0 hidden sm:block">{r.typ}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Top Artists by Votes ── */
const DE_MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function TopVotedArtistsSection() {
  const [artists, setArtists] = useState([]);
  const { t } = useTranslation();
  const now = new Date();
  const monthLabel = `${DE_MONTHS[now.getMonth()]} ${now.getFullYear()}`;

  useEffect(() => {
    api.get("/artists/top-voted-monthly?limit=5")
      .then(res => setArtists(res.data || []))
      .catch(() => {});
  }, []);

  const topArtist = artists[0] || null;

  return (
    <section
      className="py-14 border-t border-white/5"
      style={{ background: "#050505", backgroundImage: "repeating-linear-gradient(-45deg, rgba(255,255,255,0.012) 0px, rgba(255,255,255,0.012) 1px, transparent 1px, transparent 28px)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-1">{t("home.top_artists_eyebrow")} · {monthLabel}</p>
          <h2 className="text-2xl sm:text-3xl font-black font-heading text-white">{t("home.top_artists_title")}</h2>
        </div>

        {artists.length === 0 && (
          <p className="text-zinc-600 text-sm mb-4">Noch keine Votes in {monthLabel}.</p>
        )}

        {/* Artist des Monats – #1 der Rangliste */}
        {topArtist && (
          <Link
            to={`/artist/${topArtist.slug}`}
            data-testid="artist-of-month-card"
            className="group flex items-center gap-4 mb-5 p-4 rounded-2xl transition-all duration-200"
            style={{
              background: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(245,158,11,0.04) 100%)",
              border: "1px solid rgba(251,191,36,0.18)",
              boxShadow: "0 2px 24px rgba(251,191,36,0.06), inset 0 1px 0 rgba(251,191,36,0.08)",
            }}
          >
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-zinc-800"
                style={{ boxShadow: "0 0 0 2px rgba(251,191,36,0.3), 0 4px 16px rgba(0,0,0,0.5)" }}>
                {topArtist.bild_url ? (
                  <img src={topArtist.bild_url} alt={topArtist.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg font-black text-amber-400 font-heading"
                    style={{ background: "rgba(251,191,36,0.1)" }}>
                    {topArtist.name[0]}
                  </div>
                )}
              </div>
              <span className="absolute -top-2.5 -right-2 text-base select-none" title="Artist des Monats">👑</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-amber-400/80 text-xs font-bold uppercase tracking-widest mb-0.5">Artist des Monats · {monthLabel}</p>
              <p className="text-white text-base font-black font-heading truncate group-hover:text-amber-300 transition-colors">{topArtist.name}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Heart size={14} fill="currentColor" className="text-amber-400" />
              <span className="text-amber-300 text-sm font-black tabular-nums">{topArtist.vote_count}</span>
            </div>
          </Link>
        )}

        {/* Rangliste */}
        <div className="flex flex-col gap-1">
          {artists.map((a, i) => (
            <Link
              key={a.slug}
              to={`/artist/${a.slug}`}
              data-testid="top-voted-artist"
              className="flex items-center gap-4 py-3 px-3 rounded-xl hover:bg-zinc-900 transition-colors group"
            >
              <span className="text-zinc-700 text-base font-black w-5 text-right flex-shrink-0">{i + 1}</span>
              <div className="relative w-10 h-10 flex-shrink-0">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800">
                  {a.bild_url ? (
                    <img src={a.bild_url} alt={a.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm font-black text-zinc-600 font-heading">
                      {a.name[0]}
                    </div>
                  )}
                </div>
                {a.linked_user_ids?.length > 0 && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{
                      background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
                      boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 2px 6px rgba(59,130,246,0.5), 0 0 0 1.5px #050505",
                    }}>
                    <svg viewBox="0 0 12 12" fill="none" style={{ width: "7px", height: "7px" }}>
                      <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </div>
              <p className="flex-1 text-white text-sm font-semibold truncate group-hover:text-rose-300 transition-colors">
                {a.name}
              </p>
              <div className="flex items-center gap-1 text-rose-500 flex-shrink-0">
                <Heart size={12} fill="currentColor" />
                <span className="text-xs font-bold tabular-nums">{a.monthly_vote_count ?? a.vote_count}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Most Popular Releases (by votes) ── */
function MostPopularSection({ onOpen }) {
  const [releases, setReleases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation();
  const now = new Date();
  const monthLabel = `${DE_MONTHS[now.getMonth()]} ${now.getFullYear()}`;
  useEffect(() => {
    api.get("/votes/top/monthly?limit=10")
      .then(res => { setReleases(res.data || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  if (!loaded) return null;
  return (
    <section
      className="py-14 border-t border-white/5"
      style={{ background: "#070707", backgroundImage: "radial-gradient(circle at 10% 50%, rgba(225,29,72,0.05) 0%, transparent 50%)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-1">{t("home.charts_eyebrow")} · {monthLabel}</p>
            <h2 className="text-2xl sm:text-3xl font-black font-heading text-white">{t("home.charts_title")}</h2>
          </div>
          <Link to="/charts" className="text-zinc-600 hover:text-rose-400 text-xs font-semibold transition-colors flex items-center gap-1 mb-1">
            {t("home.all_charts")}
          </Link>
        </div>
        {releases.length === 0 ? (
          <p className="text-zinc-600 text-sm">Noch keine Votes in {monthLabel} – sei der Erste!</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {releases.map((r, i) => (
              <ReleaseCard key={r.id} release={r} onClick={onOpen} rank={i + 1} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Community Hype Section ── */
function HypeSectionHome({ onOpen }) {
  const [releases, setReleases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    api.get("/hype/top?limit=3")
      .then(res => { setReleases(res.data || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  if (!loaded || !releases.length) return null;

  const RANK_COLORS = { 1: "#f59e0b", 2: "#9ca3af", 3: "#d97706" };
  return (
    <section
      className="py-14 border-t border-white/5 relative overflow-hidden"
      style={{ background: "#060606", backgroundImage: "radial-gradient(circle at 50% 100%, rgba(245,158,11,0.08) 0%, transparent 60%)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-amber-500 text-xs font-bold uppercase tracking-widest mb-1 flex items-center gap-1.5">
              <Zap size={11} fill="currentColor" /> {t("home.hype_eyebrow")}
            </p>
            <h2 className="text-2xl sm:text-3xl font-black font-heading text-white">{t("home.hype_title")}</h2>
            <p className="text-zinc-600 text-xs mt-1">{t("home.hype_subtitle")}</p>
          </div>
          <Link to="/charts" className="text-zinc-600 hover:text-amber-400 text-xs font-semibold transition-colors flex items-center gap-1">
            {t("home.all_charts")}
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {releases.map((r, i) => {
            const rank = i + 1;
            return (
              <div
                key={r.id}
                onClick={() => onOpen(r)}
                data-testid={`hype-home-entry-${rank}`}
                className="group relative cursor-pointer rounded-2xl overflow-hidden bg-zinc-900 border border-white/5 hover:border-amber-500/30 transition-all duration-300"
              >
                {/* Cover */}
                <div className="aspect-square relative overflow-hidden">
                  <CoverImage
                    cover_url={r.cover_url}
                    interpret={r.interpret}
                    titel={r.titel}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  {/* Rank badge */}
                  <div
                    className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center font-black text-lg shadow-lg"
                    style={{ background: RANK_COLORS[rank], color: "#000" }}
                  >
                    {rank}
                  </div>
                  {/* Hype badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                    <Zap size={10} className="text-amber-400" fill="currentColor" />
                    <span className="text-amber-400 text-xs font-bold">{r.hype_count}</span>
                  </div>
                </div>
                {/* Info */}
                <div className="p-3">
                  <p className="text-white font-bold text-sm truncate group-hover:text-amber-300 transition-colors">{r.interpret}</p>
                  <p className="text-zinc-400 text-xs truncate">{r.titel}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Partner Section (from Settings) ─────── */
function PartnersSection({ partners }) {
  const [offset, setOffset] = useState(0);
  const VISIBLE = 4;
  const needsCarousel = (partners?.length || 0) > VISIBLE;

  // Hooks must be called before any early return
  useEffect(() => {
    if (!needsCarousel) return;
    const t = setInterval(() => {
      setOffset(o => (o + 1) % partners.length);
    }, 3000);
    return () => clearInterval(t);
  }, [needsCarousel, partners?.length]);

  if (!partners?.length) return null;

  // Visible slice (wrapping)
  const visible = needsCarousel
    ? Array.from({ length: VISIBLE }, (_, i) => partners[(offset + i) % partners.length])
    : partners;

  return (
    <section className="py-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-zinc-700 text-xs font-bold uppercase tracking-widest text-center mb-6">Partner</p>
        <div className="flex justify-center items-center gap-6 sm:gap-10">
          {visible.map((partner, i) => (
            <a
              key={`${offset}-${i}`}
              href={partner.link_url || undefined}
              target={partner.link_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid="partner-item"
              title={partner.name}
              className={`transition-all duration-300 hover:scale-105 ${partner.link_url ? "cursor-pointer" : "cursor-default"}`}
              style={{ animation: "fadeIn 0.5s ease" }}
            >
              {partner.logo_url
                ? <img src={partner.logo_url} alt={partner.name} className="w-full max-w-[180px] sm:max-w-[350px] h-auto object-contain" />
                : <span className="text-zinc-500 text-sm font-semibold">{partner.name}</span>
              }
            </a>
          ))}
        </div>
        {/* Dot indicators */}
        {needsCarousel && (
          <div className="flex justify-center gap-1.5 mt-5">
            {partners.map((_, i) => (
              <button
                key={i}
                onClick={() => setOffset(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === offset % partners.length ? "bg-zinc-400 w-3" : "bg-zinc-700"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Homepage ────────────────────────────────── */
export default function HomePage() {
  const [selectedRelease, setSelectedRelease] = useState(null);
  const { settings } = useSettings();
  const { t, i18n } = useTranslation();

  const heroImage       = settings?.hero_image_url ||
    "https://images.unsplash.com/photo-1773274157508-ddbf6074aa14?crop=entropy&cs=srgb&fm=jpg&q=85";
  const heroMobileImage = settings?.hero_mobile_image_url || heroImage;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>HUWND – Hands Up Will Never Die</title>
        <meta name="description" content="Die größte deutschsprachige Hands-Up-Releasedatenbank. Entdecke Singles, EPs, Alben und Artists aus der Hands-Up-Community." />
        <link rel="canonical" href="https://handsupwillneverdie.de/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:description" content="Die größte deutschsprachige Hands-Up-Releasedatenbank." />
        <meta property="og:url" content="https://handsupwillneverdie.de/" />
      </Helmet>
      <Header />

      {/* ── HERO ── */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Desktop image */}
          <img src={heroImage} alt="Hero"
            className="hidden sm:block w-full h-full object-cover" />
          {/* Mobile image */}
          <img src={heroMobileImage} alt="Hero"
            className="block sm:hidden w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto px-4 py-20">
          <p className="text-rose-500 text-xs font-bold uppercase tracking-[0.3em] mb-5">
            {t("home.hero_tag")}
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none tracking-tighter font-heading mb-5">
            {getLocalizedText(settings?.hero_title, i18n.language) || t("home.hero_title_fallback")}
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-10">
            {getLocalizedText(settings?.hero_subtitle, i18n.language) || t("home.hero_subtitle_fallback")}
          </p>

          {/* ── HERO SEARCH ── */}
          <HeroSearch />

          <p className="text-white/50 text-xs mt-4 drop-shadow-lg">
            {t("home.hero_scroll_hint")}
          </p>
        </div>
      </section>

      {/* ── LATEST RELEASES ── */}
      <LatestReleases onReleaseClick={setSelectedRelease} />

      {/* ── COMMUNITY HYPE (zuerst) ── */}
      <HypeSectionHome onOpen={setSelectedRelease} />

      {/* ── COMMUNITY CHARTS (votes) ── */}
      <MostPopularSection onOpen={setSelectedRelease} />

      {/* ── TOP ARTISTS (by votes) ── */}
      <TopVotedArtistsSection />

      {/* ── ARTIST SPOTLIGHT (weekly) ── */}
      <ArtistSpotlightSection onOpen={setSelectedRelease} />

      {/* ── PARTNER SPOT ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <PartnerSpot position="home" />
      </div>

      {/* ── PARTNERS (from settings) ── */}
      <PartnersSection partners={settings?.partners} />

      <Footer settings={settings} />

      {selectedRelease && (
        <ReleaseModal release={selectedRelease} onClose={() => setSelectedRelease(null)} />
      )}
    </div>
  );
}
